/**
 * Guilt-Free Money Calculation Engine
 * Pure utility functions — no side effects, no state.
 */

/**
 * Calculate the guilt-free spending money for the current month.
 * @param {number} income - Monthly take-home income
 * @param {Array} fixedExpenses - Array of { amount, frequency } objects
 * @param {Array} goals - Array of { targetAmount, savedSoFar, deadline } objects
 * @param {Array} budgets - Array of { limit } objects (category budgets)
 * @returns {object} { guiltFree, totalFixed, totalGoals, totalBudgets }
 */
export function calcGuiltFree(income, fixedExpenses = [], goals = [], budgets = []) {
    const now = new Date();
    
    const totalFixed = fixedExpenses.reduce((sum, exp) => {
        return sum + monthlyAmount(exp.amount, exp.frequency, exp.lastPaidDate, now);
    }, 0);

    const totalGoals = goals.reduce((sum, goal) => {
        return sum + calcGoalMonthly(goal.targetAmount, goal.savedSoFar, goal.deadline);
    }, 0);

    const totalBudgets = budgets.reduce((sum, b) => sum + (b.limit || 0), 0);

    const guiltFree = Math.max(0, income - totalFixed - totalGoals - totalBudgets);

    return { guiltFree, totalFixed, totalGoals, totalBudgets };
}

/**
 * Convert an expense amount to its monthly equivalent.
 * For exact frequencies > 1 month, it only returns the full amount IF it is due this exact month.
 * Otherwise, it returns 0.
 * For daily/weekly, it projects the cost over the month.
 */
function monthlyAmount(amount, frequency, lastPaidDate, currentDate = new Date()) {
    if (!lastPaidDate) return amount; // Fallback to every month if no date is set

    const lastPaid = new Date(lastPaidDate);
    const monthsDiff = (currentDate.getFullYear() - lastPaid.getFullYear()) * 12 + (currentDate.getMonth() - lastPaid.getMonth());

    switch (frequency) {
        case 'daily': return amount * 30;
        case 'weekly': return amount * 4.33;
        case 'monthly': return amount;
        case 'quarterly': return (monthsDiff > 0 && monthsDiff % 3 === 0) ? amount : 0;
        case '4_months': return (monthsDiff > 0 && monthsDiff % 4 === 0) ? amount : 0;
        case '6_months': return (monthsDiff > 0 && monthsDiff % 6 === 0) ? amount : 0;
        case 'yearly': return (monthsDiff > 0 && monthsDiff % 12 === 0) ? amount : 0;
        case '24_months': return (monthsDiff > 0 && monthsDiff % 24 === 0) ? amount : 0;
        default: return amount; // monthly fallback
    }
}

/**
 * Calculate the monthly contribution needed to reach a savings goal.
 * @returns {number} Monthly amount to set aside (0 if already met or past deadline)
 */
export function calcGoalMonthly(targetAmount, savedSoFar, deadline) {
    const remaining = targetAmount - savedSoFar;
    if (remaining <= 0) return 0;

    const monthsLeft = getMonthsUntil(deadline);
    if (monthsLeft <= 0) return remaining; // Overdue — need the full amount now

    return Math.ceil(remaining / monthsLeft);
}

/**
 * Get the number of months from today until a deadline date string.
 */
export function getMonthsUntil(deadline) {
    if (!deadline) return 0;
    const now = new Date();
    const target = new Date(deadline);
    const months = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
    return Math.max(0, months);
}

/**
 * Calculate remaining budget for a category given its limit and spending this month.
 */
export function calcBudgetRemaining(limit, spentThisMonth) {
    return Math.max(0, limit - spentThisMonth);
}

/**
 * Get spending summary from transactions.
 * Tries current calendar month first. If no transactions exist for this month,
 * falls back to the most recent month that has transaction data.
 * @param {Array} transactions - All transactions from Dexie
 * @returns {object} { totalSpent, totalIncome, byCategory, monthLabel }
 */
export function getCurrentMonthSpending(transactions) {
    const now = new Date();
    const currentPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Try current month first
    let result = sumByMonth(transactions, currentPrefix);
    if (result.totalSpent > 0 || result.totalIncome > 0) {
        return result;
    }

    // No data for current month — find the most recent month with data
    const monthSet = new Set();
    for (const txn of transactions) {
        if (txn.date && txn.date.length >= 7) {
            monthSet.add(txn.date.substring(0, 7)); // "YYYY-MM"
        }
    }

    if (monthSet.size === 0) {
        // No parseable dates — sum ALL transactions
        return sumAllTransactions(transactions);
    }

    const sortedMonths = Array.from(monthSet).sort().reverse();
    return sumByMonth(transactions, sortedMonths[0]);
}

function sumByMonth(transactions, prefix) {
    let totalSpent = 0;
    let totalIncome = 0;
    const byCategory = {};

    for (const txn of transactions) {
        if (!txn.date || !txn.date.startsWith(prefix)) continue;

        const amount = parseAmount(txn.amount);
        if (amount <= 0) continue;

        const isDebit = txn.type && txn.type.toUpperCase() === 'DEBIT';
        if (isDebit) {
            totalSpent += amount;
            const cat = txn.category || 'Uncategorized';
            byCategory[cat] = (byCategory[cat] || 0) + amount;
        } else {
            totalIncome += amount;
        }
    }

    return { totalSpent, totalIncome, byCategory };
}

function sumAllTransactions(transactions) {
    let totalSpent = 0;
    let totalIncome = 0;
    const byCategory = {};

    for (const txn of transactions) {
        const amount = parseAmount(txn.amount);
        if (amount <= 0) continue;

        const isDebit = txn.type && txn.type.toUpperCase() === 'DEBIT';
        if (isDebit) {
            totalSpent += amount;
            const cat = txn.category || 'Uncategorized';
            byCategory[cat] = (byCategory[cat] || 0) + amount;
        } else {
            totalIncome += amount;
        }
    }

    return { totalSpent, totalIncome, byCategory };
}

/**
 * Get spending for the previous month.
 */
export function getLastMonthSpending(transactions) {
    const now = new Date();
    let targetMonth = now.getMonth(); // 0-indexed, so this is "current" but we want previous
    let targetYear = now.getFullYear();
    if (targetMonth === 0) { targetMonth = 12; targetYear--; }
    // targetMonth is now 1-12 for the previous month
    const prefix = `${targetYear}-${String(targetMonth).padStart(2, '0')}`;

    let totalSpent = 0;
    for (const txn of transactions) {
        if (!txn.date || !txn.date.startsWith(prefix)) continue;

        const amount = parseAmount(txn.amount);
        const isDebit = txn.type && txn.type.toUpperCase() === 'DEBIT';
        if (isDebit && amount > 0) totalSpent += amount;
    }
    return totalSpent;
}

/**
 * Days left in the current month.
 */
export function getDaysLeftInMonth() {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return lastDay - now.getDate();
}

/**
 * Detect whether the uploaded data covers a PAST month or the CURRENT month.
 * Returns simple, user-friendly context — no "modes" exposed to the user.
 *
 * @param {Array} transactions - All transactions from Dexie
 * @returns {object} {
 *   isMidMonth: boolean,        // true if data includes current month
 *   daysLeft: number,           // days remaining in the relevant period
 *   spentSoFar: number,         // total debits in the current/active month
 *   heroLabel: string,          // e.g. "GUILT-FREE MONEY · NEXT 30 DAYS"
 *   heroSub: string,            // e.g. "Based on your March spending"
 *   pageSubtitle: string,       // e.g. "Planning April 2026" or "April 2026 — 23 days left"
 *   statementMonthLabel: string // e.g. "March" (the month the data is mostly from)
 * }
 */
export function detectSpendingContext(transactions) {
    const now = new Date();
    const currentPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const currentMonthName = now.toLocaleDateString('en-IN', { month: 'long' });
    const currentMonthYear = now.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const daysLeftInMonth = getDaysLeftInMonth();

    // Count transactions in current month vs older months
    let currentMonthCount = 0;
    let currentMonthSpent = 0;
    const olderMonths = new Set();

    for (const txn of transactions) {
        if (!txn.date || txn.date.length < 7) continue;
        const prefix = txn.date.substring(0, 7);
        if (prefix === currentPrefix) {
            currentMonthCount++;
            const isDebit = txn.type && txn.type.toUpperCase() === 'DEBIT';
            if (isDebit) currentMonthSpent += parseAmount(txn.amount);
        } else {
            olderMonths.add(prefix);
        }
    }

    // Find the most recent older month for labeling
    const sortedOlder = Array.from(olderMonths).sort().reverse();
    let statementMonthLabel = '';
    if (sortedOlder.length > 0) {
        const [y, m] = sortedOlder[0].split('-');
        statementMonthLabel = new Date(parseInt(y), parseInt(m) - 1, 1)
            .toLocaleDateString('en-IN', { month: 'long' });
    }

    // If there are current-month transactions, it's a mid-month scenario
    const isMidMonth = currentMonthCount > 0;

    if (isMidMonth) {
        return {
            isMidMonth: true,
            daysLeft: daysLeftInMonth,
            spentSoFar: currentMonthSpent,
            heroLabel: 'GUILT-FREE MONEY · LEFT THIS MONTH',
            heroSub: 'Spend freely — this is what\'s still safe',
            pageSubtitle: `${currentMonthYear} — ${daysLeftInMonth} day${daysLeftInMonth !== 1 ? 's' : ''} left`,
            statementMonthLabel: currentMonthName
        };
    } else {
        // Data is from past months — show forward-looking number
        return {
            isMidMonth: false,
            daysLeft: 30,
            spentSoFar: 0,
            heroLabel: 'GUILT-FREE MONEY · NEXT 30 DAYS',
            heroSub: statementMonthLabel
                ? `Based on your ${statementMonthLabel} spending`
                : 'Based on your recent spending',
            pageSubtitle: `Planning ${currentMonthName} ${now.getFullYear()}`,
            statementMonthLabel: statementMonthLabel || 'last month'
        };
    }
}

// --- Internal helpers ---

function parseAmount(str) {
    if (typeof str === 'number') return Math.abs(str);
    const cleaned = String(str).replace(/[₹,\s]/g, '').replace(/Rs\./g, '');
    return Math.abs(parseFloat(cleaned)) || 0;
}

